import React, { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiService } from "../../services/api";
import { useApp } from "../../contexts/AppContext";
import { t } from "../../utils/localization";
import styles from "./Marquee.module.css";

export default function Marquee() {
  const { state } = useApp();
  const textRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [animationDuration, setAnimationDuration] = useState<string>("30s");
  const [isReady, setIsReady] = useState<boolean>(false);

  // Скорость прокрутки в пикселях в секунду (можно настроить через переменную окружения)
  const scrollSpeed = parseFloat(process.env.REACT_APP_MARQUEE_SPEED || "80"); // по умолчанию 80 px/s

  const {
    data: marquees,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["marquee", { active: true }],
    queryFn: () => apiService.getMarquees({ active: true }),
    staleTime: 300000, // 5 minutes
  });

  // Объединяем все активные бегущие строки в одну
  const combinedText = marquees
    ? marquees
        .map((marquee) => {
          return state.language === "en"
            ? marquee.textEn && marquee.textEn.trim() !== ""
              ? marquee.textEn
              : marquee.textRu
            : marquee.textRu;
        })
        .join("\u00A0".repeat(180))
    : "";

  // Создаем уникальный ключ на основе текста для предотвращения наслоения
  const textKey =
    combinedText.length > 0
      ? `${combinedText.substring(0, 100)}-${marquees?.length || 0}-${
          state.language
        }`
      : "empty";

  // Вычисляем длительность анимации на основе ширины текста и скорости
  useEffect(() => {
    if (
      !textRef.current ||
      !containerRef.current ||
      !marquees ||
      marquees.length === 0 ||
      !combinedText ||
      combinedText.length === 0
    ) {
      setIsReady(false);
      return;
    }

    // Показываем текст сразу, даже если измерения еще не завершены
    setIsReady(true);

    // Используем requestAnimationFrame для измерения после рендера
    const measureAndUpdate = () => {
      const element = textRef.current;
      const container = containerRef.current;

      if (!element || !container) return;

      const containerWidth = container.offsetWidth;

      // Измеряем ширину текста без скрытия элемента
      // Используем временный элемент для измерения, чтобы не скрывать основной
      const measureElement = document.createElement("div");
      measureElement.style.cssText = `
        position: absolute;
        visibility: hidden;
        white-space: nowrap;
        font-family: Inter;
        font-size: 36px;
        font-weight: 400;
      `;
      measureElement.textContent = combinedText;
      document.body.appendChild(measureElement);

      const textWidth = measureElement.offsetWidth;
      document.body.removeChild(measureElement);

      if (textWidth === 0 || containerWidth === 0) {
        // Если измерения не удались, используем приблизительное значение
        const estimatedWidth = combinedText.length * 20; // примерная ширина символа
        const totalDistance = containerWidth + estimatedWidth;
        const duration = totalDistance / scrollSpeed;
        setAnimationDuration(`${duration}s`);
        return;
      }

      // Текст начинается справа (за пределами контейнера) и движется влево
      // Расстояние = ширина контейнера + ширина текста (для полного прохода)
      // Длительность = расстояние / скорость
      const totalDistance = containerWidth + textWidth;
      const duration = totalDistance / scrollSpeed;

      // Устанавливаем длительность в секундах
      setAnimationDuration(`${duration}s`);
    };

    // Даем браузеру время на рендер перед измерением
    requestAnimationFrame(() => {
      requestAnimationFrame(measureAndUpdate);
    });
  }, [combinedText, scrollSpeed, marquees]);

  // Принудительно перезапускаем анимацию при изменении текста
  useEffect(() => {
    if (!textRef.current || !combinedText || !isReady) {
      return;
    }

    const element = textRef.current;

    // Временно останавливаем анимацию
    element.style.animation = "none";

    // Принудительно перерисовываем
    void element.offsetWidth; // Это вызывает reflow

    // Запускаем анимацию заново
    requestAnimationFrame(() => {
      if (element) {
        element.style.animation = "";
      }
    });
  }, [textKey, animationDuration, combinedText, isReady]);

  if (isLoading) {
    return (
      <div className={styles.marqueeContainer}>
        <div className={styles.loadingSpinner}>
          {t("loading", state.language)}...
        </div>
      </div>
    );
  }

  if (error || !marquees || marquees.length === 0) {
    return (
      <div className={styles.marqueeContainer}>
        {/* No marquee available */}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={styles.marqueeContainer}>
      {/* Единый элемент для измерения и отображения */}
      {combinedText && (
        <div
          key={textKey}
          ref={textRef}
          className={styles.marqueeText}
          style={
            {
              ["--marquee-duration" as any]: animationDuration,
              visibility: isReady ? "visible" : "hidden",
            } as React.CSSProperties
          }
        >
          {combinedText}
        </div>
      )}
    </div>
  );
}
